import * as pdfjs from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { aggregateFonts, isScannedPage } from "./pdfAnalysis";
import type { EmbeddedPdfFont, LoadedPdf, PdfPageInfo, TextOverlay } from "./types";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

const makeId = (prefix: string, pageIndex: number, index: number) => `${prefix}-${pageIndex}-${index}`;

const stripSubsetPrefix = (name: string) => name.replace(/^[A-Z]{6}\+/, "");

export const classifyPdfFontName = (internalName: string, fallbackFamily = "Unknown font") => {
  const name = stripSubsetPrefix(internalName || "");
  const hint = `${name} ${fallbackFamily}`;
  const fontWeight = /bold|black|heavy|semibold|demi/i.test(hint) ? 700 as const : 400 as const;
  const fontStyle = /italic|oblique/i.test(hint) ? "italic" as const : "normal" as const;
  let family = name.replace(/[-_,]?(bold|black|heavy|semibold|demi|italic|oblique|regular|roman|mt)+$/ig, "").trim();
  if (/arial/i.test(hint)) family = "Arial";
  else if (/helvetica|nimbus.?sans/i.test(hint)) family = "Helvetica";
  else if (/liberation.?sans/i.test(hint)) family = "Liberation Sans";
  else if (/times|liberation.?serif|georgia|garamond|cambria|serif/i.test(hint)) family = /liberation/i.test(hint) ? "Liberation Serif" : "Times Roman";
  else if (/courier|mono/i.test(hint)) family = "Courier";
  else if (!family || /^font[\d_-]/i.test(family)) family = fallbackFamily;
  return { family, fontWeight, fontStyle };
};

interface PdfJsFontObject {
  data?: Uint8Array | ArrayBuffer;
  fallbackName?: string;
  loadedName?: string;
  name?: string;
}

const fontBytes = (data?: Uint8Array | ArrayBuffer) => {
  if (!data) return undefined;
  return data instanceof Uint8Array ? data.slice() : new Uint8Array(data.slice(0));
};

const extractPage = async (pageIndex: number, page: pdfjs.PDFPageProxy, documentKey: string): Promise<{ page: PdfPageInfo; embeddedFonts: Record<string, EmbeddedPdfFont> }> => {
  const viewport = page.getViewport({ scale: 1 });
  const content = await page.getTextContent();
  await page.getOperatorList();
  let itemIndex = 0;
  const textItems: TextOverlay[] = [];
  const embeddedFonts: Record<string, EmbeddedPdfFont> = {};

  for (const entry of content.items) {
    if (!("str" in entry)) continue;
    const item = entry;
    const matrix = pdfjs.Util.transform(viewport.transform, item.transform);
    const style = content.styles[item.fontName];
    let nativeFont: PdfJsFontObject | undefined;
    try {
      nativeFont = page.commonObjs.get(item.fontName) as PdfJsFontObject | undefined;
    } catch {
      nativeFont = undefined;
    }
    const internalName = nativeFont?.name || item.fontName;
    const traits = classifyPdfFontName(internalName, nativeFont?.fallbackName || style?.fontFamily || "Unknown font");
    const embeddedFontId = nativeFont?.data ? item.fontName : undefined;
    const embeddedFontFamily = embeddedFontId ? `PDFLite_${documentKey}_${item.fontName}`.replace(/[^a-z0-9_-]/gi, "_") : undefined;
    if (embeddedFontId && !embeddedFonts[embeddedFontId]) {
      embeddedFonts[embeddedFontId] = {
        id: embeddedFontId,
        internalName,
        family: traits.family,
        cssFamily: embeddedFontFamily!,
        fontWeight: traits.fontWeight,
        fontStyle: traits.fontStyle,
        data: fontBytes(nativeFont?.data),
      };
    }
    const fontSize = Math.max(1, Math.hypot(matrix[2], matrix[3]));
    const ascent = style?.ascent ?? 0.8;
    const width = Math.max(2, item.width);
    const height = Math.max(fontSize, item.height || 0);
    textItems.push({
      id: makeId("native", pageIndex, itemIndex++),
      pageIndex,
      source: "native",
      text: item.str,
      x: matrix[4],
      y: matrix[5] - fontSize * ascent,
      width,
      height,
      fontName: internalName,
      fontFamily: traits.family,
      fontSize,
      fontWeight: traits.fontWeight,
      fontStyle: traits.fontStyle,
      embeddedFontId,
      embeddedFontFamily,
    });
  }

  const nativeCharacterCount = textItems.reduce((sum, item) => sum + item.text.trim().length, 0);
  return { page: {
    index: pageIndex,
    width: viewport.width,
    height: viewport.height,
    rotation: viewport.rotation,
    viewportTransform: [...viewport.transform],
    nativeCharacterCount,
    isScanned: isScannedPage(nativeCharacterCount, textItems.filter((item) => item.text.trim()).length),
    textItems,
    fonts: aggregateFonts(textItems),
  }, embeddedFonts };
};

export const loadPdf = async (bytes: Uint8Array, name: string): Promise<LoadedPdf> => {
  const loadingTask = pdfjs.getDocument({ data: bytes.slice(), fontExtraProperties: true });
  const document = await loadingTask.promise;
  const documentKey = (document.fingerprints[0] || `${bytes.byteLength}`).slice(0, 20);
  const extracted = await Promise.all(
    Array.from({ length: document.numPages }, async (_, pageIndex) =>
      extractPage(pageIndex, await document.getPage(pageIndex + 1), documentKey),
    ),
  );
  const embeddedFonts = Object.assign({}, ...extracted.map((result) => result.embeddedFonts)) as Record<string, EmbeddedPdfFont>;
  if (typeof FontFace !== "undefined" && typeof window !== "undefined") {
    await Promise.all(Object.values(embeddedFonts).map(async (font) => {
      if (!font.data) return;
      try {
        const face = new FontFace(font.cssFamily, font.data.slice().buffer, { weight: String(font.fontWeight), style: font.fontStyle });
        window.document.fonts.add(await face.load());
      } catch (error) {
        console.warn(`Embedded font ${font.internalName} could not be loaded.`, error);
      }
    }));
  }
  return { name, bytes, document, pages: extracted.map((result) => result.page), embeddedFonts };
};

export const renderPdfPage = async (
  document: pdfjs.PDFDocumentProxy,
  pageIndex: number,
  canvas: HTMLCanvasElement,
  scale: number,
  signal?: AbortSignal,
) => {
  const page = await document.getPage(pageIndex + 1);
  if (signal?.aborted) return null;
  const viewport = page.getViewport({ scale });
  const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("Canvas is not available.");
  canvas.width = Math.floor(viewport.width * pixelRatio);
  canvas.height = Math.floor(viewport.height * pixelRatio);
  canvas.style.width = `${viewport.width}px`;
  canvas.style.height = `${viewport.height}px`;
  const renderTask = page.render({
    canvasContext: context,
    viewport,
    transform: pixelRatio === 1 ? undefined : [pixelRatio, 0, 0, pixelRatio, 0, 0],
  });
  const abort = () => renderTask.cancel();
  signal?.addEventListener("abort", abort, { once: true });
  try {
    await renderTask.promise;
  } catch (error) {
    if (!signal?.aborted) throw error;
  } finally {
    signal?.removeEventListener("abort", abort);
  }
  return viewport;
};

export const renderPageForOcr = async (document: pdfjs.PDFDocumentProxy, pageIndex: number, scale = 2.25) => {
  const page = await document.getPage(pageIndex + 1);
  const viewport = page.getViewport({ scale });
  const canvas = window.document.createElement("canvas");
  const context = canvas.getContext("2d", { alpha: false, willReadFrequently: true });
  if (!context) throw new Error("Canvas is not available.");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  await page.render({ canvasContext: context, viewport }).promise;
  return { canvas, scale };
};
